import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import noop from 'lib/function/noop'
import { transcribeAudio, type TranscribeAudioParams } from 'tripPlanner/api/transcriptions'
import { getExtensionFromMimeType, isVoiceInputSupported } from '../utils/audio'

export type VoiceInputError = 'microphone-unavailable' | 'transcription-failed'

type RecorderStatus = 'idle' | 'starting' | 'recording' | 'stopping'

interface Options {
  onTranscription: (text: string) => void
  onError: (error: VoiceInputError) => void
}

const WAVEFORM_BAR_COUNT = 48
const FILL_INTERVAL_MS = 70
const STOP_GRACE_PERIOD_MS = 300

const createEmptyWaveform = () => Array.from({ length: WAVEFORM_BAR_COUNT }, () => 0)

export function useMicrophoneTranscriber({ onTranscription, onError }: Options) {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  // Mirrors `status` for checks inside async callbacks, which see a stale render value.
  const statusRef = useRef<RecorderStatus>('idle')
  const [waveformLevels, setWaveformLevels] = useState<Array<number>>(() => createEmptyWaveform())
  const [filledBars, setFilledBars] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Array<Blob>>([])
  const recordedMimeTypeRef = useRef<string>('')
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const recordingStartTimeRef = useRef<number | null>(null)
  const shouldUploadRef = useRef(false)
  const nextWaveformUpdateRef = useRef<number | null>(null)
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const uploadAbortControllerRef = useRef<AbortController | null>(null)

  const transcription = useMutation({
    mutationFn: (params: TranscribeAudioParams) => transcribeAudio(params),
  })
  const { mutate: transcribe, reset: resetTranscription, isLoading: isUploading } = transcription

  const updateStatus = useCallback((nextStatus: RecorderStatus) => {
    statusRef.current = nextStatus
    setStatus(nextStatus)
  }, [])

  const stopVisualizer = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [])

  const clearPendingStop = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current)
      stopTimeoutRef.current = null
    }
  }, [])

  const resetRecordingVisuals = useCallback(() => {
    setWaveformLevels(createEmptyWaveform())
    setFilledBars(0)
    nextWaveformUpdateRef.current = null
  }, [])

  const handleUpload = useCallback((audio: Blob, filename: string) => {
    const abortController = new AbortController()
    uploadAbortControllerRef.current = abortController
    // Per-call callbacks don't run once the composer unmounts or the upload is reset.
    transcribe({ audio, filename, signal: abortController.signal }, {
      onSuccess: ({ text }) => {
        if (text.trim()) {
          onTranscription(text)
        } else {
          onError('transcription-failed')
        }
      },
      onError: () => {
        if (!abortController.signal.aborted) {
          onError('transcription-failed')
        }
      },
      onSettled: () => {
        uploadAbortControllerRef.current = null
        resetRecordingVisuals()
      },
    })
  }, [onError, onTranscription, resetRecordingVisuals, transcribe])

  const cleanupAudioGraph = useCallback((preserveWaveform = false) => {
    stopVisualizer()
    recordingStartTimeRef.current = null

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect()
      sourceNodeRef.current = null
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
    }

    dataArrayRef.current = null

    const audioContext = audioContextRef.current
    audioContextRef.current = null
    if (audioContext) {
      audioContext.close().catch(noop)
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop())
      mediaStreamRef.current = null
    }

    if (!preserveWaveform) {
      resetRecordingVisuals()
    }
  }, [resetRecordingVisuals, stopVisualizer])

  const updateWaveform = useCallback(() => {
    const analyser = analyserRef.current
    const dataArray = dataArrayRef.current
    let level = 0

    if (analyser && dataArray) {
      analyser.getByteTimeDomainData(dataArray)
      let peak = 0
      for (let i = 0; i < dataArray.length; i += 1) {
        const value = Math.abs((dataArray[i] - 128) / 128)
        if (value > peak) {
          peak = value
        }
      }
      level = Math.min(1, peak * 1.6)
    }

    const now = Date.now()
    let shouldShift = false

    if (nextWaveformUpdateRef.current === null && recordingStartTimeRef.current) {
      nextWaveformUpdateRef.current = recordingStartTimeRef.current + FILL_INTERVAL_MS
    }

    if (nextWaveformUpdateRef.current !== null && now >= nextWaveformUpdateRef.current) {
      shouldShift = true
      nextWaveformUpdateRef.current = now + FILL_INTERVAL_MS
    }

    setWaveformLevels(prev => {
      const next = prev.slice()
      if (next.length === 0) {
        return prev
      }
      const lastIndex = next.length - 1
      next[lastIndex] = level

      if (shouldShift) {
        next.shift()
        next.push(level)
      }
      return next
    })

    if (shouldShift) {
      setFilledBars(prev => (prev >= WAVEFORM_BAR_COUNT ? prev : prev + 1))
    }

    animationFrameRef.current = requestAnimationFrame(updateWaveform)
  }, [])

  const startRecording = useCallback(async() => {
    // A local, so TS doesn't carry this narrowing of statusRef past the await below.
    const initialStatus = statusRef.current
    if (initialStatus !== 'idle' || isUploading) {
      return
    }

    if (!isVoiceInputSupported()) {
      onError('microphone-unavailable')
      return
    }

    updateStatus('starting')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      updateStatus('idle')
      onError('microphone-unavailable')
      return
    }

    // Cancelled (chat switch, unmount) while the permission prompt was open.
    if (statusRef.current !== 'starting') {
      stream.getTracks().forEach(track => track.stop())
      return
    }

    try {
      mediaStreamRef.current = stream
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      recordedMimeTypeRef.current = ''
      shouldUploadRef.current = false

      const audioContext = new AudioContext()
      const sourceNode = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      const dataArray: Uint8Array<ArrayBuffer> = new Uint8Array(analyser.fftSize)
      sourceNode.connect(analyser)

      audioContextRef.current = audioContext
      sourceNodeRef.current = sourceNode
      analyserRef.current = analyser
      dataArrayRef.current = dataArray

      const startTime = Date.now()
      recordingStartTimeRef.current = startTime
      resetRecordingVisuals()
      nextWaveformUpdateRef.current = startTime + FILL_INTERVAL_MS

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          if (!recordedMimeTypeRef.current && event.data.type) {
            recordedMimeTypeRef.current = event.data.type
          }
        }
      }

      mediaRecorder.onstop = () => {
        const shouldUpload = shouldUploadRef.current
        clearPendingStop()
        cleanupAudioGraph(shouldUpload)
        updateStatus('idle')

        const mimeType = recordedMimeTypeRef.current || mediaRecorder.mimeType || 'audio/webm'
        const extension = getExtensionFromMimeType(mimeType)
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })

        mediaRecorderRef.current = null
        recordedMimeTypeRef.current = ''
        audioChunksRef.current = []
        shouldUploadRef.current = false

        if (shouldUpload && audioBlob.size > 0) {
          handleUpload(audioBlob, `recording.${extension}`)
        } else if (shouldUpload) {
          resetRecordingVisuals()
          onError('transcription-failed')
        }
      }

      mediaRecorder.start()
      updateStatus('recording')
      animationFrameRef.current = requestAnimationFrame(updateWaveform)
    } catch {
      cleanupAudioGraph()
      mediaRecorderRef.current = null
      updateStatus('idle')
      onError('microphone-unavailable')
    }
  }, [cleanupAudioGraph, clearPendingStop, handleUpload, isUploading, onError, resetRecordingVisuals, updateStatus, updateWaveform])

  const finishRecording = useCallback((shouldUpload: boolean) => {
    shouldUploadRef.current = shouldUpload
    const recorder = mediaRecorderRef.current
    clearPendingStop()

    if (recorder && recorder.state !== 'inactive') {
      if (shouldUpload) {
        updateStatus('stopping')
        // Keep a small trailing buffer after the user taps stop so STT has
        // enough end-of-utterance context to avoid intermittently dropping the final word.
        stopTimeoutRef.current = setTimeout(() => {
          stopTimeoutRef.current = null
          if (recorder.state !== 'inactive') {
            recorder.stop()
          }
        }, STOP_GRACE_PERIOD_MS)
        return
      }

      recorder.stop()
      return
    }

    cleanupAudioGraph(shouldUpload)
    updateStatus('idle')
  }, [cleanupAudioGraph, clearPendingStop, updateStatus])

  const submitRecording = useCallback(() => {
    finishRecording(true)
  }, [finishRecording])

  /** Stops a recording, or abandons a transcription in flight, without producing a transcript. */
  const cancelRecording = useCallback(() => {
    uploadAbortControllerRef.current?.abort()
    uploadAbortControllerRef.current = null
    resetTranscription()
    finishRecording(false)
  }, [finishRecording, resetTranscription])

  useEffect(() => () => {
    clearPendingStop()
    shouldUploadRef.current = false
    statusRef.current = 'idle'
    uploadAbortControllerRef.current?.abort()
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    } else {
      cleanupAudioGraph()
    }
  }, [clearPendingStop, cleanupAudioGraph])

  return {
    isRecording: status === 'recording',
    isTranscribing: status === 'stopping' || isUploading,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  }
}
